import {css, StyleSheet} from 'aphrodite';
import React, {useState, Fragment} from 'react';

import {deleteAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {
  ANALYSIS_TOOL_SPACING,
  SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
} from 'analysis-tool/AnalysisToolConstants';
import {
  AnalysisGraphQL,
  GetAnalysisDataFrame,
  GetAnalysisPipelineStep,
  GetAnalysisStepData,
} from 'analysis-tool/AnalysisTypes';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getAdditionalDataFrameMetadataModalContent from 'analysis-tool/data-frame-metadata/getDataFrameMetadataModalContent';
import {checkCanModifyStep, checkCanReplaceStep} from 'analysis-tool/EditStepHelpers';
import {getDataFrameHeaderMenuOptions} from 'analysis-tool/header-options/GetDataFrameHeaderMenuOptions';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {isStepInProgress} from 'analysis-tool/isAnalysisRunning';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import DataFrameActionButton from 'data-frames/toolbar/DataFrameActionButton';
import {createRenderAddChartViewButton} from 'data-frames/toolbar/toolbar-actions/renderAddChartViewButton';
import {GetPersistedDataFrame_dataFrame_DataFrameNode as GetPersistedDataFrameDataFrame} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  DataFrameTableWrapperOverflowOption,
  RowSelectionProps,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PresentationalPersistedDataFrameTableWrapper,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {StepReference} from 'pipelines/network/PipelineDAO';
import {
  getChildStepsForDataFrame,
  getOriginOrTerminalStep,
  getTableLineageAncestorSteps,
} from 'pipelines/StepLineageUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {withLoadingScreen} from 'util/Promises';

import DataFrameViewTransformationSidebar from '../components/DataFrameViewTransformationSidebar';
import StepLockedWarningBanner from '../components/StepLockedWarningBanner';
import {openFlagRowsModal} from '../flag-rows/FlagRowsModal';
import {getFlaggedRowsFromDataFrame} from '../flag-rows/FlagRowUtil';
import getDataFrameRenderOverrides from '../flag-rows/getDataFrameRenderOverrides';
import {getSaveToResultsOverflowOption, getTransformationErrorText} from './MainViewHelpers';
import {AnalysisMainViewItem, AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

export const FAILED_STEP_MESSAGE = 'This action is unavailable because of an error';

function getReplaceTableOverflowOption(
  analysis: AnalysisGraphQL,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void,
  originStep: GetAnalysisPipelineStep
): DataFrameTableWrapperOverflowOption | null {
  if (!isAnalysisReadOnly(analysis)) {
    const {canEdit, message} = checkCanReplaceStep(analysis, originStep);

    return {
      isDisabled: !canEdit,
      tooltipTitle: message ?? null,
      icon: 'arrow-right-arrow-left',
      title: 'Replace table',
      onSelect: () => {
        onChangeSelectedItem({
          type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD,
          stepIdToReplace: originStep.id,
        });
      },
    };
  }

  return null;
}

function getDeleteTableOverflowOption(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  clientStateId: string
): DataFrameTableWrapperOverflowOption | null {
  if (!isAnalysisReadOnly(analysis)) {
    const {canEdit, message} = checkCanModifyStep(analysis, step.id, 'table', 'delete');
    const disabledOptions = {isDisabled: !canEdit, tooltipTitle: message || null};

    return {
      ...disabledOptions,
      icon: 'delete',
      title: 'Delete table',
      onSelect: () => {
        runConfirmDeleteModal({typeName: 'table'}).then((confirmed) => {
          if (!confirmed) {
            return;
          }
          const parentTableStep = getTableLineageAncestorSteps(analysis, step)[0];
          withLoadingScreen(
            deleteAnalysisSteps(
              analysis,
              {
                stepIds: [parentTableStep.id],
                behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
              },
              clientStateId
            )
          );
        });
      },
    };
  }

  return null;
}

function getAdditionalOverflowOptions(
  analysis: AnalysisGraphQL,
  clientStateId: string,
  step: GetAnalysisPipelineStep,
  dataFrame: GetAnalysisDataFrame,
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void
): Array<DataFrameTableWrapperOverflowOption> {
  const dataFrameId = dataFrame.id;
  const outputStepData = getOutputStepDataForDataFrame(step, dataFrameId);
  if (outputStepData == null) {
    assert(
      step.statusCode !== PipelineStepStatusCode.SUCCEEDED,
      "Data frame's step has not output step data that contains the data frame, but is succeeded"
    );
  }

  const additionalOverflowOptions: Array<DataFrameTableWrapperOverflowOption> = [];

  const saveToResultsOption = getSaveToResultsOverflowOption(
    analysis,
    step,
    dataFrame.id,
    onChangeSelectedItem,
    onOpenStepInitializationView
  );

  if (saveToResultsOption != null) {
    additionalOverflowOptions.push(saveToResultsOption);
  }

  const replaceTableOption = getReplaceTableOverflowOption(
    analysis,
    onChangeSelectedItem,
    getOriginOrTerminalStep(analysis, step)
  );
  if (replaceTableOption != null) {
    additionalOverflowOptions.push(replaceTableOption);
  }
  const deleteTableOption = getDeleteTableOverflowOption(analysis, step, clientStateId);

  if (deleteTableOption != null) {
    additionalOverflowOptions.push(deleteTableOption);
  }

  return additionalOverflowOptions;
}

function getRowSelectionProps({
  analysis,
  step,
  dataFrame,
  stepData,
  viewSteps,
  outputDataReferences,
}: {
  analysis: AnalysisGraphQL;
  step: GetAnalysisPipelineStep;
  dataFrame: GetPersistedDataFrameDataFrame;
  viewSteps: Array<GetAnalysisPipelineStep>;
  stepData: GetAnalysisStepData;
  outputDataReferences: Array<{stepId: string; stepDataName: string}> | undefined;
}): RowSelectionProps | undefined {
  const flaggedRows = getFlaggedRowsFromDataFrame(analysis, step, dataFrame.dataFrameJson.rows);
  const canAddNewSteps =
    viewSteps.every((step) => step.statusCode === PipelineStepStatusCode.SUCCEEDED) &&
    !hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);

  return !isAnalysisReadOnly(analysis) && canAddNewSteps
    ? {
        onSelectRows: (selectedRowIds: Set<string>, isAddingFlag: boolean) => {
          openFlagRowsModal({
            analysis,
            selectedRowIds,
            flaggedRowIds: flaggedRows,
            isAddingFlag,
            parentStepReference: {
              stepReference: {
                stepId: step.id,
                type: StepReference.STEP_ID,
              },
              stepDataName: stepData.name,
            },
            outputDataReferences,
          });
        },
        flaggedRows,
      }
    : undefined;
}

export default function DataFrameView({
  analysis,
  clientStateId,
  onChangeSelectedItem,
  onOpenStepInitializationView,
  selectedItem,
  onChangeSelectedRightSidebarItem,
  step,
}: MainViewComponentProps<AnalysisMainViewItemType.DATA_FRAME>): JSX.Element {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const dataFrameId = selectedItem.dataFrameId;
  const stepDatas = step.outputStepDatas.filter(({kind}) => kind === PipelineStepDataKind.DATASET);
  assert(stepDatas.length > 0, 'Expected to render a step with at least 1 DATASET output step data.');
  const stepData =
    stepDatas.length === 1
      ? stepDatas[0]
      : stepDatas.find((stepData) => stepData.dataFrames.some(({id}) => id === dataFrameId));
  // If this functionality is needed, consider sending `DataReference` - like objects to  `DataFrameView` instead
  // of just step + dataFrameId.
  assert(
    stepData != null,
    "Expected dataFrameId to be in the given step's output when the step has multiple output step datas."
  );

  const viewSteps = getTableLineageAncestorSteps(analysis, step);

  // Find the step at the origin on the lineage, so it can be included in the metadata
  const viewOriginStep = viewSteps[0];

  const dataReference = {
    stepId: selectedItem.stepId,
    stepDataName: stepData.name,
  };
  let outputDataReferences: Array<{stepId: string; stepDataName: string}> | undefined;
  const childSteps = getChildStepsForDataFrame(analysis, step, dataFrameId);
  if (childSteps.length > 0) {
    outputDataReferences = childSteps.flatMap((childStep) =>
      childStep.inputStepDatas
        .filter((stepData) => stepData.dataFrames.some((dataFrame) => dataFrame.id === dataFrameId))
        .map((inputStepData) => ({
          stepId: childStep.id,
          stepDataName: inputStepData.name,
        }))
    );
  }

  const headerMenuOptions = getDataFrameHeaderMenuOptions(
    analysis,
    null,
    clientStateId,
    step,
    dataReference,
    dataFrameId,
    outputDataReferences
  );

  const maybeRenderEditButton = (): JSX.Element =>
    isSidebarOpen ? (
      <Fragment key="EditTransformationsButton" />
    ) : (
      <DataFrameActionButton
        icon="pen-to-square"
        onClick={() => {
          setIsSidebarOpen(true);
        }}
        title={isArchived(analysis) ? 'View transforms' : 'Edit transforms'}
        key="EditTransformationsButton"
      />
    );

  // Render Presentational data frame rather than Querying data frame so that we can handle loading
  // state in this file. Otherwise, the view will have a border that contains a throbber, which looks awkward.
  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
      renderCachedDataWhenRefetching={false}
    >
      {({dataFrame}, result) => (
        <LoadingOverlay
          isFaded={true}
          isLoading={isStepInProgress(step) || result.type === QueryResultType.PENDING || result.loading}
        >
          <div
            className={css(styles.wrapper)}
            {...getTestProps({component: 'DataFrameView', element: 'root'})}
          >
            {isInaccessible(dataFrame) ? (
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text="You do not have access to this table."
              />
            ) : (
              <div className={css(styles.tableAndSideBarWrapper)}>
                <div className={css(styles.bannerAndTableWrapper)}>
                  {step.statusCode === PipelineStepStatusCode.FAILED && (
                    <div className={css(styles.banner)}>
                      <InlineBanner
                        bannerType={InlineBannerType.ERROR}
                        text={getTransformationErrorText(step.statusMessage, step.definition.stepType)}
                        dataTestAttrs={{component: 'ErrorBanner', element: 'root'}}
                      />
                    </div>
                  )}
                  <div className={css(styles.banner)}>
                    <StepLockedWarningBanner analysis={analysis} step={step} />
                  </div>
                  <div className={css(styles.tableWrapper)}>
                    <PresentationalPersistedDataFrameTableWrapper
                      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
                      rowSelectionProps={getRowSelectionProps({
                        analysis,
                        step,
                        dataFrame,
                        viewSteps,
                        stepData,
                        outputDataReferences,
                      })}
                      renderIsExcludedColumn={false}
                      clientStateId={clientStateId}
                      additionalOverflowOptions={
                        !result.loading
                          ? getAdditionalOverflowOptions(
                              analysis,
                              clientStateId,
                              step,
                              dataFrame,
                              onChangeSelectedItem,
                              onOpenStepInitializationView
                            )
                          : []
                      }
                      additionalActionButtons={
                        !isAnalysisReadOnly(analysis) &&
                        // PARAMETER_OPTIMIZATION_CONFIGURATION step is view only upon creation and should not have any additional action buttons
                        step.definition.stepType !== PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION
                          ? [
                              createRenderAddChartViewButton(
                                analysis,
                                step.id,
                                dataFrame.id,
                                onOpenStepInitializationView,
                                onChangeSelectedRightSidebarItem
                              ),
                              maybeRenderEditButton,
                            ]
                          : undefined
                      }
                      persistedDataFrame={dataFrame}
                      dataFrameRenderOverrides={getDataFrameRenderOverrides(analysis, step)}
                      additionalMetadataModalContent={getAdditionalDataFrameMetadataModalContent(
                        analysis.steps.find(({id}) => id === viewOriginStep.id)!
                      )}
                      headerMenuOptions={headerMenuOptions}
                    />
                  </div>
                </div>
                <DataFrameViewTransformationSidebar
                  analysis={analysis}
                  step={step}
                  isOpen={isSidebarOpen}
                  setIsOpen={setIsSidebarOpen}
                />
              </div>
            )}
          </div>
        </LoadingOverlay>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    overflowY: 'auto',
    marginBottom: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  bannerAndTableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
    overflowX: 'hidden',
  },
  banner: {
    margin: `${ANALYSIS_TOOL_SPACING}px ${ANALYSIS_TOOL_SPACING}px 0`,
  },
  tableAndSideBarWrapper: {
    display: 'flex',
    height: '100%',
  },
  tableWrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    display: 'flex',
    flexDirection: 'column',
  },
});
